import React from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router';

import { SerpsListItem } from '../../types';
import { useCanDisplayLinkbox } from './use-can-display-linkbox';

import { Container, ListWrapper, Tag, Title } from './tag-list.styles';

type ListItem = { name: JSX.Element | string; seoName: string; count?: number; url?: string };

type TagListProps = {
  headerCopyKey: string;
  headerCopyValues?: Record<string, string>;
  list?: ListItem[];
  serpsList?: SerpsListItem[];
  route?: string;
  getRouteParams?: (item: ListItem) => Record<string, string | undefined>;
  showEmptyResults?: 'hide' | 'show';
  showCount?: 'hide' | 'show';
};

function serpItemsToTagListItems(items: SerpsListItem[]): ListItem[] {
  return items
    .filter((item) => item.link)
    .map((item) => ({
      name: item.link?.label || '',
      seoName: '',
      count: item.count,
      url: item.link?.url,
    }));
}

export const TagList: React.FC<TagListProps> = ({
  headerCopyKey,
  headerCopyValues = {},
  list = [],
  serpsList = [],
  route,
  getRouteParams,
  showEmptyResults = 'hide',
  showCount = 'show',
}) => {
  const canDisplay = useCanDisplayLinkbox(headerCopyKey);

  if (!canDisplay) {
    return null;
  }

  const displayCount = showCount === 'show';
  const displayEmptyResults = showEmptyResults === 'show';

  // do not mutate list, make a copy
  let listToRender = list.length > 0 ? [...list] : serpItemsToTagListItems(serpsList);
  listToRender = displayEmptyResults ? listToRender : listToRender.filter((item) => item.count && item.count > 0);

  if (listToRender.length <= 0) {
    return null;
  }

  const sortedList = displayCount
    ? listToRender.sort((itemA, itemB) => -((itemA.count || 0) - (itemB.count || 0)))
    : listToRender;

  return (
    <>
      <Container>
        <Title size="large" sizeConfined="xlarge">
          <FormattedMessage id={headerCopyKey} values={headerCopyValues} />
        </Title>
        <ListWrapper>
          {sortedList.map((item) => {
            let url = '';

            if (item.url) {
              url = item.url;
            } else {
              if (!(route && getRouteParams)) {
                throw new Error('TagList: route and getRouteParams are required when item.url is not provided.');
              } else {
                url = generatePath(route, getRouteParams(item));
              }
            }

            const count = item.count || 0;

            return (
              <Tag key={url} href={url}>
                {item.name}
                {displayCount && count > 0 ? ` (${count})` : ''}
              </Tag>
            );
          })}
        </ListWrapper>
      </Container>
    </>
  );
};
